/**
 * Llojet e pushimit — një katalog i vetëm, i njëjtë me tabelën `leave_types`.
 *
 * ── Pse ekziston ─────────────────────────────────────────────────────────────
 * Tri ekrane (kërkesa e re, AddHoliday, EditHoliday) mbanin secili listën e vet
 * të koduar me dorë, me numra të shpikur veçmas nga tabela. Ato numra bien mbi
 * të njëjtën hapësirë: `holidays.holiday_type` përcillet si `leave_types.id` te
 * rojtari i balancës (`guardLeaveBalance` → `leaveAdjustmentBalance`), dhe
 * `leave_balances.leave_type_id` ka çelës të huaj drejt `leave_types`.
 *
 * Përplasja ishte reale — vetëm 1 dhe 2 përputheshin:
 *
 *   id | leave_types (baza)          | lista e vjetër e koduar
 *   ---|-----------------------------|-------------------------
 *    3 | Leja e Lehonisë             | Pushim martesor
 *    4 | Leja e Atësisë              | Pushim pa pagesë
 *    5 | Pushim pa Pagë              | Pushim i jashtëzakonshëm
 *    6 | Leja Martesore              | Pushim me pagesë
 *    7 | Vdekja e anëtarit të familjes | Dhënie gjaku
 *
 * Prandaj lehonia "nuk ekzistonte": nuk ishte thjesht e palistuar, numri i saj
 * ishte zënë nga pushimi martesor. Asnjë rresht i `holidays` nuk përdorte 3–10
 * (vetëm 1 dhe 2), ndaj rreshtimi me tabelën nuk prish të dhëna ekzistuese.
 *
 * ── Rregulli ────────────────────────────────────────────────────────────────
 * Çdo ekran që zgjedh, etiketon ose ngjyros një lloj pushimi merret prej këtu.
 * Nëse tabela `leave_types` ndryshon, ndryshon ky skedar — jo pesë të tjerë.
 */

/**
 * `allowanceKey` lexohet nga cilësimet e kompanisë kur ekziston; `defaultDays`
 * është ajo që vlen kur cilësimi mungon. `Infinity` = pa kufi vjetor.
 */
export const LEAVE_TYPES = [
  { id: 11, code: 'overtime_hours', label: 'Pushim nga orët shtesë', short: 'Overtime', color: '#0F766E', defaultDays: Infinity },
  { id: 1,  code: 'annual',      label: 'Pushim vjetor',          short: 'Vjetor',        color: '#2563EB', allowanceKey: 'AnnualLeaveAllowance',        defaultDays: 20 },
  { id: 2,  code: 'medical',     label: 'Pushim mjekësor',        short: 'Mjekësor',      color: '#DC2626', allowanceKey: 'MedicalLeaveAllowance',       defaultDays: 20,       requiresCert: true },
  { id: 3,  code: 'maternity',   label: 'Leja e lehonisë',        short: 'Lehoni',        color: '#DB2777', allowanceKey: 'MaternityLeaveAllowance',     defaultDays: 365,      requiresCert: true },
  { id: 4,  code: 'paternity',   label: 'Leja e atësisë',         short: 'Atësi',         color: '#7C3AED', allowanceKey: 'PaternityLeaveAllowance',     defaultDays: 3 },
  { id: 5,  code: 'unpaid',      label: 'Pushim pa pagesë',       short: 'Pa pagesë',     color: '#9333EA', allowanceKey: 'UnpaidLeaveAllowance',        defaultDays: Infinity },
  { id: 6,  code: 'marital',     label: 'Leja martesore',         short: 'Martesor',      color: '#059669', allowanceKey: 'MaritalLeaveAllowance',       defaultDays: 5 },
  { id: 7,  code: 'bereavement', label: 'Vdekje në familje',      short: 'Vdekje',        color: '#475569', allowanceKey: 'BereavementLeaveAllowance',   defaultDays: 5 },
  { id: 8,  code: 'blood',       label: 'Dhënie gjaku',           short: 'Dhënie gjaku',  color: '#0891B2', allowanceKey: 'BloodDonationLeaveAllowance', defaultDays: 1 },
  // 9 = "Festë Zyrtare": e vendos kompania te kalendari, nuk e kërkon punonjësi,
  // ndaj mungon te lista e kërkesave por mbetet e njohur për etiketim.
  { id: 9,  code: 'public_holiday', label: 'Festë zyrtare',       short: 'Festë',         color: '#64748B', defaultDays: Infinity, requestable: false },
  { id: 10, code: 'work_injury', label: 'Lëndim/sëmundje pune',   short: 'Lëndim pune',   color: '#B45309', defaultDays: Infinity, requiresCert: true },
];

/** Ato që një punonjës mund t'i kërkojë vetë. */
export const REQUESTABLE_LEAVE_TYPES = LEAVE_TYPES.filter((t) => t.requestable !== false);

export const LEAVE_TYPE_BY_ID = LEAVE_TYPES.reduce((m, t) => { m[t.id] = t; return m; }, {});

/**
 * Llojet e vërteta vijnë nga tabela `leave_types`, jo nga lista më lart.
 *
 * ── Pse ─────────────────────────────────────────────────────────────────────
 * Kjo listë ishte shkruar me dorë sipas Ligjit të Punës dhe u desh të ishte e
 * njëjtë me bazën. Nuk është: te prodhimi numri 3 është "Pa pagese", 4
 * "Martese", 5 "Lehoni", 6 "Vdekje familjare" — ndërsa këtu 3 shkruhet "Leja e
 * lehonisë" dhe 4 "Leja e atësisë". Një kërkesë ekzistuese me lloj 4 shfaqet si
 * atësi kur në bazë do të thotë martesë.
 *
 * Baza është burimi: atë e sheh raporti, pagesa dhe ligji — dhe kompania i
 * shton vetë llojet e reja. Prandaj lista lart mbetet vetëm rezervë për çastin
 * para se përgjigjja të mbërrijë, ose kur serveri s'i kthen dot.
 *
 * Objektet ndryshohen NË VEND (`splice` / fshirje çelësash) me qëllim: nëntëmbëdhjetë
 * vende të tjera i kanë importuar tashmë këto referenca, dhe një zëvendësim i
 * tyre do t'i linte ato duke lexuar listën e vjetër përgjithmonë.
 */
let hydrated = false;

export async function hydrateLeaveTypes(fetchJson) {
  if (hydrated) return LEAVE_TYPES;
  try {
    const rows = await fetchJson();
    const list = (Array.isArray(rows) ? rows : [])
      .map((t) => ({
        id: Number(t.Id ?? t.id),
        code: String(t.Code ?? t.code ?? ''),
        label: t.Name ?? t.name ?? '',
        requiresCert: Boolean(t.RequiresCert ?? t.requires_cert),
        defaultDays: Number(t.MaxDaysYear ?? t.max_days_year) || undefined,
      }))
      .filter((t) => t.id && t.label);
    if (!list.length) return LEAVE_TYPES;

    // Ruaj ngjyrën dhe emrin e shkurtër kur lloji njihet; për një lloj të ri
    // ngjyra vjen nga rrota më poshtë, që dy lloje të reja të mos duken njësoj.
    const merged = list.map((t) => {
      const known = LEAVE_TYPE_BY_ID[t.id];
      return {
        ...t,
        short: t.label,
        color: known?.color || NEW_TYPE_COLORS[t.id % NEW_TYPE_COLORS.length],
        allowanceKey: known?.allowanceKey,
        requestable: t.code === 'public_holiday' ? false : (known?.requestable !== false),
      };
    });

    LEAVE_TYPES.splice(0, LEAVE_TYPES.length, ...merged);
    REQUESTABLE_LEAVE_TYPES.splice(
      0, REQUESTABLE_LEAVE_TYPES.length,
      ...merged.filter((t) => t.requestable !== false),
    );
    Object.keys(LEAVE_TYPE_BY_ID).forEach((k) => { delete LEAVE_TYPE_BY_ID[k]; });
    Object.keys(LEAVE_TYPE_LABEL).forEach((k) => { delete LEAVE_TYPE_LABEL[k]; });
    merged.forEach((t) => { LEAVE_TYPE_BY_ID[t.id] = t; LEAVE_TYPE_LABEL[t.id] = t.label; });

    hydrated = true;
  } catch {
    /* Pa rrjet mbetet lista e ngurtë — më mirë emra të përafërt se asnjë ekran. */
  }
  return LEAVE_TYPES;
}

const NEW_TYPE_COLORS = ['#2563EB', '#DC2626', '#DB2777', '#7C3AED', '#9333EA', '#059669', '#475569', '#0891B2'];

export const leaveType = (id) => LEAVE_TYPE_BY_ID[Number(id)] || null;
export const leaveTypeLabel = (id) => leaveType(id)?.label || '';
export const leaveTypeShort = (id) => leaveType(id)?.short || leaveTypeLabel(id);
export const leaveTypeColor = (id) => leaveType(id)?.color || '#6B7280';

/** Etiketat si hartë e thjeshtë — për kodin që pret `{ [id]: 'Emri' }`. */
export const LEAVE_TYPE_LABEL = LEAVE_TYPES.reduce((m, t) => { m[t.id] = t.label; return m; }, {});

/** Opsionet e një `<Select>`: `{ value, label }`. */
export const leaveTypeOptions = () =>
  REQUESTABLE_LEAVE_TYPES.map((t) => ({ value: t.id, label: t.label }));

/** Opsionet e formularëve të vjetër, që presin `{ id, name }`. */
export const leaveTypeChoices = () =>
  REQUESTABLE_LEAVE_TYPES.map((t) => ({ id: t.id, name: t.short }));

/** Ditët vjetore për një lloj, sipas cilësimeve të kompanisë kur i ka. */
export function leaveAllowance(id, settings) {
  const t = leaveType(id);
  if (!t) return Infinity;
  const raw = settings && t.allowanceKey ? Number(settings[t.allowanceKey]) : NaN;
  return Number.isFinite(raw) && raw > 0 ? raw : t.defaultDays;
}

/** Numrat sipas kodit — që asnjë shifër e zhveshur të mos mbetet nëpër ekrane. */
export const LEAVE_TYPE_ID = LEAVE_TYPES.reduce((m, t) => { m[t.code] = t.id; return m; }, {});

/** Vetëm pushimi vjetor e ngarkon balancën vjetore — shih leaveSummary.js. */
export const ANNUAL_LEAVE_TYPE_ID = LEAVE_TYPE_ID.annual;
/** Lehonia ka periudhat e veta 6+3+3 — services/maternityLeave.js. */
export const MATERNITY_LEAVE_TYPE_ID = LEAVE_TYPE_ID.maternity;

/* ─── Kush mund ta kërkojë një lloj pushimi ────────────────────────────────
 *
 * Lehonia është e punonjëses. Këtu rri VETËM ajo që i duhet zgjedhësit të
 * punonjësit për ta ngushtuar listën; vendimi i vërtetë merret te serveri
 * (`backend/services/maternityEligibility.js`), sepse një kërkesë mund të vijë
 * edhe nga app-i mobil ose nga një formular i vjetër i mbetur hapur.
 *
 * Këto dy grupe pasqyrojnë atë skedar fjalë për fjalë. Kur njëri ndryshon,
 * ndryshon edhe tjetri — janë e njëjta politikë e shkruar dy herë sepse
 * browser-i dhe serveri s'e ndajnë dot të njëjtin modul.
 */
const FEMALE_TOKENS = new Set(['0', 'F', 'FEMER', 'FEMËR', 'FEMALE', 'FEM', 'W', 'WOMAN', 'GRUA']);
const MALE_TOKENS   = new Set(['1', 'M', 'MASHKULL', 'MALE', 'MAN', 'BURRE', 'BURRË']);

/**
 * → 'F' | 'M' | null.
 *
 * `null` do të thotë "e paplotësuar", jo "mashkull". Në bazë shumica e
 * punëtorëve e kanë fushën bosh, dhe një rregull që i heq ata nga lista do të
 * bllokonte kërkesa krejt të ligjshme për një fushë që askush s'e ka mbushur.
 */
export function normalizeGender(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().toUpperCase().replace(/\s+/g, '');
  if (!s) return null;
  if (FEMALE_TOKENS.has(s)) return 'F';
  if (MALE_TOKENS.has(s)) return 'M';
  return null;
}

/** Gjinia e një rreshti punëtori, në cilëndo formë që vjen. */
export const genderOfWorker = (w) => normalizeGender(w?.gender ?? w?.Gender ?? null);

/**
 * Punonjëset që mund të marrin lehoni, plus ata që u lanë jashtë dhe pse.
 *
 * `hiddenUnknown` numërohet veçmas nga `hiddenMale`: e para është një fushë e
 * paplotësuar që dikush duhet ta plotësojë te profili, e dyta është rregulli
 * që punon. Bashkuar, HR-ja s'do ta dinte kurrë pse dikush mungon nga lista.
 */
export function splitForMaternity(workers = []) {
  const eligible = [];
  let hiddenMale = 0;
  let hiddenUnknown = 0;
  for (const w of workers) {
    const g = genderOfWorker(w);
    if (g === 'F') eligible.push(w);
    else if (g === 'M') hiddenMale += 1;
    else hiddenUnknown += 1;
  }
  return { eligible, hiddenMale, hiddenUnknown };
}
